import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { KIT_ITEM_KIND_FROM_GQL, KIT_ITEM_KIND_TO_GQL } from '@/shared/graphql/enums';
import { centsToMajorString, majorStringToCents } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

const KIT_DETAIL_QUERY = gql`
  query KitDetail($id: ID!) {
    kit(id: $id) {
      id
      code
      name
      items {
        id
        item_kind
        product_id
        service_id
        quantity
        unit_price_cents_override
        price_rule_id
      }
    }
  }
`;

const KIT_EDITOR_LOOKUPS_QUERY = gql`
  query KitEditorLookups {
    products(limit: 200, status_filter: ACTIVE) {
      items { id code name }
    }
    services(limit: 200, status_filter: ACTIVE) {
      items { id code name }
    }
  }
`;

const PRICE_RULES_FOR_SUBJECT_QUERY = gql`
  query PriceRulesForSubject($subject_kind: PriceRuleSubjectKind!, $subject_id: ID!) {
    priceRules(subject_kind: $subject_kind, subject_id: $subject_id, status: ACTIVE, limit: 100) {
      items { id note priority price_cents }
    }
  }
`;

const ADD_KIT_ITEM_MUTATION = gql`
  mutation AddKitItem($input: AddKitItemInput!) {
    addKitItem(input: $input) { id }
  }
`;

const UPDATE_KIT_ITEM_MUTATION = gql`
  mutation UpdateKitItem($id: ID!, $input: UpdateKitItemInput!) {
    updateKitItem(id: $id, input: $input) { id }
  }
`;

const REMOVE_KIT_ITEM_MUTATION = gql`
  mutation RemoveKitItem($id: ID!) {
    removeKitItem(id: $id)
  }
`;

interface KitItemRowRaw {
  id: string;
  item_kind: string;
  product_id: string | null;
  service_id: string | null;
  quantity: string;
  unit_price_cents_override: string | null;
  price_rule_id: string | null;
}

interface KitItemRow extends Omit<KitItemRowRaw, 'item_kind'> {
  item_kind: 'product' | 'service';
}

interface KitDetailResponse {
  kit: { id: string; code: string; name: string; items: KitItemRowRaw[] };
}

interface LookupsResponse {
  products: { items: { id: string; code: string; name: string }[] };
  services: { items: { id: string; code: string; name: string }[] };
}

interface PriceRulesResponse {
  priceRules: { items: { id: string; note: string | null; priority: number; price_cents: string }[] };
}

interface KitItemsEditorProps {
  kit_id: string;
  kit_name: string;
  onClose: () => void;
}

const DECIMAL3_RE = /^\d+(\.\d{1,3})?$/;
const MAJOR_MONEY_RE = /^\d+(\.\d{1,2})?$/;

/**
 * Stage A — inline editor for `kit_items`. Stage E extends it with two optional
 * columns per row: flat per-kit price override and scoped price-rule picker.
 * The two are mutually exclusive (enforced by `KitItemsService`).
 */
export function KitItemsEditor({ kit_id, kit_name, onClose }: KitItemsEditorProps): JSX.Element {
  const { t } = useTranslation();
  const { data: kit_data, refetch } = useQuery<KitDetailResponse>(KIT_DETAIL_QUERY, {
    variables: { id: kit_id },
    fetchPolicy: 'cache-and-network',
  });
  const { data: lookups } = useQuery<LookupsResponse>(KIT_EDITOR_LOOKUPS_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const [addItem, { loading: adding }] = useMutation(ADD_KIT_ITEM_MUTATION);
  const [updateItem] = useMutation(UPDATE_KIT_ITEM_MUTATION);
  const [removeItem] = useMutation(REMOVE_KIT_ITEM_MUTATION);

  const items: KitItemRow[] = (kit_data?.kit.items ?? []).map((item) => {
    const mapped_kind =
      KIT_ITEM_KIND_FROM_GQL[item.item_kind as keyof typeof KIT_ITEM_KIND_FROM_GQL] ??
      item.item_kind;
    return {
      ...item,
      item_kind: mapped_kind === 'service' ? 'service' : 'product',
    };
  });
  const products = lookups?.products.items ?? [];
  const services = lookups?.services.items ?? [];

  const [draft_kind, setDraftKind] = useState<'product' | 'service'>('product');
  const [draft_id, setDraftId] = useState<string>('');
  const [draft_qty, setDraftQty] = useState<string>('1');
  const [draft_override, setDraftOverride] = useState<string>('');
  const [draft_rule_id, setDraftRuleId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const onAdd = async (): Promise<void> => {
    setError(null);
    if (!draft_id) {
      setError(t('kit_items.error_pick_item'));
      return;
    }
    if (!DECIMAL3_RE.test(draft_qty)) {
      setError(t('kit_items.error_invalid_qty'));
      return;
    }
    if (draft_override && draft_rule_id) {
      setError(t('kit_items.error_override_and_rule_exclusive'));
      return;
    }
    let override_cents: number | undefined;
    if (draft_override) {
      if (!MAJOR_MONEY_RE.test(draft_override)) {
        setError(t('kit_items.error_invalid_override'));
        return;
      }
      override_cents = Number(majorStringToCents(draft_override));
    }
    try {
      await addItem({
        variables: {
          input: {
            kit_id,
            item_kind: KIT_ITEM_KIND_TO_GQL[draft_kind],
            product_id: draft_kind === 'product' ? draft_id : undefined,
            service_id: draft_kind === 'service' ? draft_id : undefined,
            quantity: draft_qty,
            unit_price_cents_override: override_cents,
            price_rule_id: draft_rule_id || undefined,
          },
        },
      });
      setDraftId('');
      setDraftQty('1');
      setDraftOverride('');
      setDraftRuleId('');
      await refetch();
    } catch (err) {
      setError(err instanceof Error ? mapError(err.message, t) : 'add_failed');
    }
  };

  const onChangeQty = async (item_id: string, qty: string): Promise<void> => {
    if (!DECIMAL3_RE.test(qty)) return;
    try {
      await updateItem({ variables: { id: item_id, input: { quantity: qty } } });
      await refetch();
    } catch {
      // ignore — toast surface left for follow-up
    }
  };

  const onChangeOverride = async (item_id: string, value: string, has_rule: boolean): Promise<void> => {
    setError(null);
    if (has_rule && value) {
      setError(t('kit_items.error_override_and_rule_exclusive'));
      return;
    }
    try {
      if (!value) {
        await updateItem({
          variables: { id: item_id, input: { clear_unit_price_cents_override: true } },
        });
      } else {
        if (!MAJOR_MONEY_RE.test(value)) {
          setError(t('kit_items.error_invalid_override'));
          return;
        }
        const cents = Number(majorStringToCents(value));
        await updateItem({
          variables: { id: item_id, input: { unit_price_cents_override: cents } },
        });
      }
      await refetch();
    } catch (err) {
      setError(err instanceof Error ? mapError(err.message, t) : 'update_failed');
    }
  };

  const onChangeRule = async (item_id: string, rule_id: string, has_override: boolean): Promise<void> => {
    setError(null);
    if (has_override && rule_id) {
      setError(t('kit_items.error_override_and_rule_exclusive'));
      return;
    }
    try {
      if (!rule_id) {
        await updateItem({ variables: { id: item_id, input: { clear_price_rule_id: true } } });
      } else {
        await updateItem({ variables: { id: item_id, input: { price_rule_id: rule_id } } });
      }
      await refetch();
    } catch (err) {
      setError(err instanceof Error ? mapError(err.message, t) : 'update_failed');
    }
  };

  const onRemove = async (item_id: string): Promise<void> => {
    try {
      await removeItem({ variables: { id: item_id } });
      await refetch();
    } catch {
      // ignore
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="max-h-[90vh] w-full max-w-5xl overflow-auto">
        <CardHeader>
          <CardTitle>{t('kit_items.title', { name: kit_name })}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('kit_items.subtitle')}</p>
          <p className="text-xs text-muted-foreground">{t('kit_items.helper_multi_row')}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t('kit_items.kind')}</th>
                  <th className="px-3 py-2">{t('kit_items.item')}</th>
                  <th className="px-3 py-2 text-right">{t('kit_items.quantity')}</th>
                  <th className="px-3 py-2 text-right">{t('kit_items.price_override_label')}</th>
                  <th className="px-3 py-2">{t('kit_items.price_rule_picker_label')}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">
                      {t('kit_items.empty')}
                    </td>
                  </tr>
                ) : null}
                {items.map((it) => (
                  <KitItemRowView
                    key={it.id}
                    row={it}
                    products={products}
                    services={services}
                    onChangeQty={(qty) => {
                      void onChangeQty(it.id, qty);
                    }}
                    onChangeOverride={(value) => {
                      void onChangeOverride(it.id, value, it.price_rule_id !== null);
                    }}
                    onChangeRule={(rule_id) => {
                      void onChangeRule(it.id, rule_id, it.unit_price_cents_override !== null);
                    }}
                    onRemove={() => {
                      void onRemove(it.id);
                    }}
                  />
                ))}
                <tr>
                  <td className="px-3 py-2">
                    <select
                      value={draft_kind}
                      onChange={(e) => {
                        setDraftKind(e.target.value as 'product' | 'service');
                        setDraftId('');
                        setDraftRuleId('');
                      }}
                      className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                    >
                      <option value="product">{t('kit_items.kind_product')}</option>
                      <option value="service">{t('kit_items.kind_service')}</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={draft_id}
                      onChange={(e) => {
                        setDraftId(e.target.value);
                        setDraftRuleId('');
                      }}
                      className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                    >
                      <option value="">{t('kit_items.pick_placeholder')}</option>
                      {(draft_kind === 'product' ? products : services).map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.code} — {opt.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Input
                      className="ml-auto h-8 w-24 text-right font-mono"
                      value={draft_qty}
                      onChange={(e) => setDraftQty(e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Input
                      className="ml-auto h-8 w-24 text-right font-mono"
                      placeholder="0.00"
                      value={draft_override}
                      onChange={(e) => setDraftOverride(e.target.value)}
                      disabled={Boolean(draft_rule_id)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <RulePickerCell
                      subject_kind={draft_kind}
                      subject_id={draft_id || null}
                      value={draft_rule_id}
                      onChange={setDraftRuleId}
                      disabled={Boolean(draft_override)}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        void onAdd();
                      }}
                      disabled={adding}
                    >
                      <Plus className="h-4 w-4" aria-hidden />
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}
          <ConsumptionPreview items={items} products={products} services={services} />
        </CardContent>
        <CardFooter className="justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

interface KitItemRowViewProps {
  row: KitItemRow;
  products: { id: string; code: string; name: string }[];
  services: { id: string; code: string; name: string }[];
  onChangeQty: (qty: string) => void;
  onChangeOverride: (value: string) => void;
  onChangeRule: (rule_id: string) => void;
  onRemove: () => void;
}

function KitItemRowView({
  row,
  products,
  services,
  onChangeQty,
  onChangeOverride,
  onChangeRule,
  onRemove,
}: KitItemRowViewProps): JSX.Element {
  const { t } = useTranslation();
  const [qty, setQty] = useState<string>(row.quantity);
  const [override_str, setOverrideStr] = useState<string>(
    row.unit_price_cents_override ? centsToMajorString(row.unit_price_cents_override) : '',
  );
  useEffect(() => setQty(row.quantity), [row.quantity]);
  useEffect(
    () =>
      setOverrideStr(
        row.unit_price_cents_override ? centsToMajorString(row.unit_price_cents_override) : '',
      ),
    [row.unit_price_cents_override],
  );

  const label =
    row.item_kind === 'product'
      ? products.find((p) => p.id === row.product_id)?.name ?? '—'
      : services.find((s) => s.id === row.service_id)?.name ?? '—';
  const code =
    row.item_kind === 'product'
      ? products.find((p) => p.id === row.product_id)?.code ?? ''
      : services.find((s) => s.id === row.service_id)?.code ?? '';

  const subject_id = row.item_kind === 'product' ? row.product_id : row.service_id;
  const has_rule = row.price_rule_id !== null;
  const has_override = row.unit_price_cents_override !== null;

  return (
    <tr>
      <td className="px-3 py-2">
        <Badge variant="muted">{t(`kit_items.kind_${row.item_kind}`)}</Badge>
      </td>
      <td className="px-3 py-2">
        <span className="font-mono text-xs text-muted-foreground">{code}</span>
        <span className="ml-2">{label}</span>
      </td>
      <td className="px-3 py-2 text-right">
        <Input
          className="ml-auto h-8 w-24 text-right font-mono"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onBlur={() => {
            if (qty !== row.quantity) onChangeQty(qty);
          }}
        />
      </td>
      <td className="px-3 py-2 text-right">
        <Input
          className={`ml-auto h-8 w-24 text-right font-mono ${has_rule ? 'opacity-50' : ''}`}
          value={override_str}
          placeholder="—"
          onChange={(e) => setOverrideStr(e.target.value)}
          onBlur={() => {
            if (override_str !== (row.unit_price_cents_override ? centsToMajorString(row.unit_price_cents_override) : '')) {
              onChangeOverride(override_str);
            }
          }}
          disabled={has_rule}
        />
      </td>
      <td className="px-3 py-2">
        <RulePickerCell
          subject_kind={row.item_kind}
          subject_id={subject_id}
          value={row.price_rule_id ?? ''}
          onChange={onChangeRule}
          disabled={has_override}
        />
      </td>
      <td className="px-3 py-2 text-right">
        <Button type="button" size="sm" variant="ghost" onClick={onRemove}>
          <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
        </Button>
      </td>
    </tr>
  );
}

interface RulePickerCellProps {
  subject_kind: 'product' | 'service';
  subject_id: string | null;
  value: string;
  onChange: (rule_id: string) => void;
  disabled?: boolean;
}

function RulePickerCell({
  subject_kind,
  subject_id,
  value,
  onChange,
  disabled,
}: RulePickerCellProps): JSX.Element {
  const { t } = useTranslation();
  const [load, { data, loading }] = useLazyQuery<PriceRulesResponse>(PRICE_RULES_FOR_SUBJECT_QUERY);

  useEffect(() => {
    if (!subject_id) return;
    void load({
      variables: {
        subject_kind: subject_kind === 'product' ? 'PRODUCT' : 'SERVICE',
        subject_id,
      },
    });
  }, [load, subject_kind, subject_id]);

  const rules = data?.priceRules.items ?? [];

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || !subject_id || loading}
      className={`w-full rounded-md border border-input bg-background px-2 py-1 text-sm ${disabled ? 'opacity-50' : ''}`}
    >
      <option value="">{t('kit_items.price_rule_none')}</option>
      {rules.map((r) => (
        <option key={r.id} value={r.id}>
          {r.note ?? r.id.slice(0, 8)} ({centsToMajorString(r.price_cents)})
        </option>
      ))}
    </select>
  );
}

function ConsumptionPreview({
  items,
  products,
  services,
}: {
  items: KitItemRow[];
  products: { id: string; code: string; name: string }[];
  services: { id: string; code: string; name: string }[];
}): JSX.Element {
  const { t } = useTranslation();
  if (items.length === 0) return <Label>{t('kit_items.preview_empty')}</Label>;
  return (
    <div className="space-y-1">
      <Label>{t('kit_items.preview_title')}</Label>
      <ul className="space-y-1 text-xs text-muted-foreground">
        {items.map((it) => {
          const label =
            it.item_kind === 'product'
              ? products.find((p) => p.id === it.product_id)?.name ?? '—'
              : services.find((s) => s.id === it.service_id)?.name ?? '—';
          const price_hint = it.unit_price_cents_override
            ? ` — ${t('kit_items.price_override_label')}: ${centsToMajorString(it.unit_price_cents_override)}`
            : it.price_rule_id
              ? ` — ${t('kit_items.price_rule_picker_label')}`
              : '';
          return (
            <li key={it.id}>
              {it.quantity} × {label}
              {price_hint}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function mapError(message: string, t: (k: string) => string): string {
  if (message.includes('kit_item_override_and_rule_exclusive'))
    return t('kit_items.error_override_and_rule_exclusive');
  if (message.includes('kit_item_rule_subject_mismatch'))
    return t('kit_items.error_rule_subject_mismatch');
  if (message.includes('kit_item_override_negative')) return t('kit_items.error_invalid_override');
  if (message.includes('feature_disabled')) return t('kit_items.error_feature_disabled');
  return message;
}
